// #tag: VAULT DOCUMENT UPLOAD AND SCAN PIPELINE
// #purpose: Processes student file uploads, camera page captures, image filters, PDF conversions, and automatic metadata tagging.
import { imageBlobToPdf, pdfFirstPageToBlob } from './pdfConvert';
import { smartScanDocumentSafe } from './documentScan';

export const VAULT_UPLOAD_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf';

export type ScanFilter = 'original' | 'enhance' | 'bw';

export interface VaultPageDraft {
  id: string;
  blob: Blob;
  previewUrl: string;
  previewImageUrl?: string;
  sourceFileName?: string;
  filter: ScanFilter;
  rotation: number;
  autoCropped?: boolean;
  scanMethod?: 'perspective' | 'trim' | 'none';
  originalBlob?: Blob;
}

export function deriveNameFromFileName(fileName: string, mimeType: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  if (base && !/^document$/i.test(base)) return base;
  return generateSmartFileName('Document', mimeType);
}

export function formatUploadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function generateSmartFileName(subcategory: string, mimeType: string): string {
  const date = new Date().toISOString().slice(0, 10);
  const base = subcategory.trim() || 'Document';
  const ext = mimeType === 'application/pdf'
    ? 'pdf'
    : mimeType.includes('png') ? 'png'
    : mimeType.includes('webp') ? 'webp'
    : 'jpg';
  return `${base} (${date}).${ext}`;
}

export function inferAutoTags(categoryId: string, subcategory: string): string[] {
  const tags = new Set<string>();
  const sub = subcategory.toLowerCase();

  if (categoryId === 'identity') {
    tags.add('Identity');
    tags.add('Government');
    tags.add('KYC');
    if (/aadhaar|pan|passport|voter|licence|license|driving/.test(sub)) tags.add('Permanent');
  } else if (categoryId === 'education') {
    tags.add('Academic');
    tags.add('Education');
  } else if (categoryId === 'certificates') {
    tags.add('Certificate');
    tags.add('Government');
  } else if (categoryId === 'photos') {
    tags.add('Photo');
  } else if (categoryId === 'exam_documents') {
    tags.add('Exam');
  }

  return Array.from(tags);
}

export function findDuplicateDocument(
  documents: Array<{ name: string; subcategory: string; categoryId: string }>,
  name: string,
  subcategory: string,
): { name: string; subcategory: string } | null {
  const normalized = name.trim().toLowerCase();
  const sub = subcategory.trim().toLowerCase();
  const match = documents.find(
    (doc) => doc.name.trim().toLowerCase() === normalized
      || (doc.subcategory.trim().toLowerCase() === sub && normalized.includes(sub)),
  );
  return match ? { name: match.name, subcategory: match.subcategory } : null;
}

export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

function isImageUpload(file: File | Blob, fileName?: string): boolean {
  const type = file.type || '';
  if (type.startsWith('image/')) return true;
  const name = fileName ?? (file instanceof File ? file.name : '');
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(name);
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

export async function applyScanFilter(
  blob: Blob,
  filter: ScanFilter,
  rotation = 0,
  brightness = 1,
  contrast = 1,
): Promise<Blob> {
  if (filter === 'original' && rotation === 0 && brightness === 1 && contrast === 1) {
    return blob;
  }

  const img = await loadImageFromBlob(blob);
  const radians = (rotation * Math.PI) / 180;
  const swap = rotation === 90 || rotation === 270;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? img.naturalHeight : img.naturalWidth;
  canvas.height = swap ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(radians);
  ctx.filter = filter === 'bw'
    ? 'grayscale(1) contrast(1.15)'
    : filter === 'enhance'
      ? 'contrast(1.12) brightness(1.06)'
      : `brightness(${brightness}) contrast(${contrast})`;
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (next) => (next ? resolve(next) : reject(new Error('Enhancement failed'))),
      'image/jpeg',
      0.92,
    );
  });
}

export async function combinePagesToPdf(pages: Blob[]): Promise<Blob> {
  if (pages.length === 0) throw new Error('No pages to combine');
  if (pages.length === 1 && pages[0].type === 'application/pdf') return pages[0];

  const { jsPDF } = await import('jspdf');
  let doc: InstanceType<typeof jsPDF> | null = null;

  for (const pageBlob of pages) {
    const blob = pageBlob.type === 'application/pdf'
      ? pageBlob
      : await applyScanFilter(pageBlob, 'original');

    if (pageBlob.type === 'application/pdf') {
      if (pages.length === 1) return pageBlob;
      continue;
    }

    const img = await loadImageFromBlob(blob);
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const orientation = w >= h ? 'landscape' : 'portrait';
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    });

    if (!doc) {
      doc = new jsPDF({ orientation, unit: 'px', format: [w, h] });
      doc.addImage(dataUrl, 'JPEG', 0, 0, w, h);
    } else {
      doc.addPage([w, h], orientation);
      doc.addImage(dataUrl, 'JPEG', 0, 0, w, h);
    }
  }

  if (!doc) {
    return imageBlobToPdf(pages[0]);
  }
  return doc.output('blob');
}

export async function readBlobAsBase64WithProgress(
  blob: Blob,
  onProgress?: (percent: number) => void,
): Promise<string> {
  onProgress?.(5);
  const buffer = await blob.arrayBuffer();
  onProgress?.(40);
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    for (let j = 0; j < slice.length; j += 1) {
      binary += String.fromCharCode(slice[j]);
    }
    onProgress?.(40 + Math.round((i / bytes.length) * 45));
  }
  onProgress?.(90);
  const encoded = btoa(binary);
  onProgress?.(100);
  return encoded;
}

export async function filesToPageDrafts(files: File[]): Promise<VaultPageDraft[]> {
  const drafts: VaultPageDraft[] = [];
  for (const file of files) {
    if (file.type === 'application/pdf') {
      const previewBlob = await pdfFirstPageToBlob(file);
      drafts.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        blob: file,
        previewUrl: URL.createObjectURL(file),
        previewImageUrl: URL.createObjectURL(previewBlob),
        sourceFileName: file.name,
        filter: 'original',
        rotation: 0,
      });
      continue;
    }

    if (!isImageUpload(file)) continue;

    const scanned = await smartScanDocumentSafe(file, { enhance: true });
    const cropped = scanned.method === 'perspective' || scanned.method === 'trim';
    const blob = cropped ? scanned.blob : await applyScanFilter(file, 'enhance');

    drafts.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      blob,
      previewUrl: URL.createObjectURL(blob),
      sourceFileName: file.name,
      filter: 'enhance',
      rotation: 0,
      autoCropped: cropped,
      scanMethod: scanned.method,
      originalBlob: file,
    });
  }
  return drafts;
}

export function revokePageDrafts(pages: VaultPageDraft[]) {
  for (const page of pages) {
    URL.revokeObjectURL(page.previewUrl);
    if (page.previewImageUrl) URL.revokeObjectURL(page.previewImageUrl);
  }
}

export async function resolveUploadFile(
  pages: VaultPageDraft[],
  saveAsPdf: boolean,
): Promise<{ blob: Blob; fileName: string; mimeType: string }> {
  if (pages.length === 0) throw new Error('No file selected');

  if (pages.length === 1 && pages[0].blob.type === 'application/pdf') {
    const blob = pages[0].blob;
    const fileName = pages[0].sourceFileName || 'document.pdf';
    return {
      blob,
      fileName,
      mimeType: 'application/pdf',
    };
  }

  if (saveAsPdf || pages.length > 1) {
    const processed = await Promise.all(
      pages.map((p) => applyScanFilter(p.blob, p.filter, p.rotation)),
    );
    const pdf = await combinePagesToPdf(processed);
    return { blob: pdf, fileName: 'document.pdf', mimeType: 'application/pdf' };
  }

  const blob = await applyScanFilter(pages[0].blob, pages[0].filter, pages[0].rotation);
  const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
  return { blob, fileName: `document.${ext}`, mimeType: blob.type || 'image/jpeg' };
}

export async function rescanPageDraft(page: VaultPageDraft): Promise<VaultPageDraft> {
  const source = page.originalBlob ?? page.blob;
  if (!isImageUpload(source)) return page;

  const scanned = await smartScanDocumentSafe(source, { enhance: true });
  const cropped = scanned.method === 'perspective' || scanned.method === 'trim';
  const blob = cropped ? scanned.blob : await applyScanFilter(source, page.filter, page.rotation);
  URL.revokeObjectURL(page.previewUrl);
  return {
    ...page,
    blob,
    previewUrl: URL.createObjectURL(blob),
    autoCropped: cropped,
    scanMethod: scanned.method,
    filter: cropped ? 'enhance' : page.filter,
    rotation: cropped ? 0 : page.rotation,
    originalBlob: page.originalBlob ?? source,
  };
}

export function applyManualCropToPage(page: VaultPageDraft, croppedBlob: Blob): VaultPageDraft {
  URL.revokeObjectURL(page.previewUrl);
  return {
    ...page,
    blob: croppedBlob,
    previewUrl: URL.createObjectURL(croppedBlob),
    autoCropped: true,
    scanMethod: 'perspective',
    filter: 'enhance',
    rotation: 0,
    originalBlob: page.originalBlob ?? page.blob,
  };
}

export function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
